"use client"
import { AlertCircle, Calendar, CheckCircle, Clock, Database, Download, FileText, RefreshCw, Upload } from 'lucide-react';
import React, { useEffect, useState } from 'react';

interface BackupSchedule {
  id: string;
  name: string;
  frequency: 'daily' | 'weekly' | 'monthly';
  time: string;
  isActive: boolean;
  lastRun?: string;
  nextRun?: string;
}

interface ExportHistory {
  id: string;
  type: string;
  filename: string;
  size: string;
  createdAt: string;
  status: 'completed' | 'failed' | 'in-progress';
}

export default function BackupRestore() {
  const [selectedExportType, setSelectedExportType] = useState<string>('');
  const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('csv');
  const [isExporting, setIsExporting] = useState(false);
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [backupSchedules, setBackupSchedules] = useState<BackupSchedule[]>([]);
  const [exportHistory, setExportHistory] = useState<ExportHistory[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [restoreMode, setRestoreMode] = useState<'merge' | 'overwrite'>('merge');
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // Fetch data on component mount
  useEffect(() => {
    fetchBackupSchedules();
    fetchExportHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchBackupSchedules() {
    try {
      const response = await fetch('/api/admin/backup-restore/schedules');

      if (!response.ok) {
        throw new Error(`Failed to fetch schedules: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && Array.isArray(data.schedules)) {
        setBackupSchedules(data.schedules.map((schedule: {
          id: string;
          name: string;
          frequency: 'daily' | 'weekly' | 'monthly';
          time: string;
          isActive: boolean;
          lastRun?: string;
          nextRun?: string;
        }) => ({
          id: schedule.id,
          name: schedule.name,
          frequency: schedule.frequency,
          time: schedule.time,
          isActive: schedule.isActive,
          lastRun: schedule.lastRun ? new Date(schedule.lastRun).toLocaleString() : undefined,
          nextRun: schedule.nextRun ? new Date(schedule.nextRun).toLocaleString() : undefined
        })));
      } else {
        console.warn('Invalid schedules data format');
      }
    } catch (error) {
      console.error('Error fetching backup schedules:', error);
      showMessage('error', 'Failed to fetch backup schedules');
    }
  }

  async function fetchExportHistory() {
    try {
      const response = await fetch('/api/admin/backup-restore/export');

      if (!response.ok) {
        console.warn(`Failed to fetch export history: ${response.status}`);
        return;
      }

      const data = await response.json();
      if (data.success && Array.isArray(data.history)) {
        setExportHistory(data.history.map((item: {
          id: string;
          type: string;
          filename: string;
          size: string;
          createdAt: string;
          status: 'completed' | 'failed' | 'in-progress';
        }) => ({
          id: item.id,
          type: item.type,
          filename: item.filename,
          size: item.size,
          createdAt: new Date(item.createdAt).toLocaleString(),
          status: item.status
        })));
      } else {
        console.warn('Invalid export history data format');
      }
    } catch (error) {
      console.error('Error fetching export history:', error);
      // Don't show error message for history fetch failures
    }
  }

  function showMessage(type: 'success' | 'error', text: string) {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  }

  const exportTypes = [
    { value: 'blogs', label: 'Blogs', icon: FileText },
    { value: 'users', label: 'Users', icon: Database },
    { value: 'faqs', label: 'FAQs', icon: FileText },
    { value: 'media', label: 'Media Library', icon: Database },
    { value: 'analytics', label: 'Analytics Data', icon: Database }
  ];

  const handleExport = async () => {
    if (!selectedExportType) return;

    setIsExporting(true);

    try {
      const response = await fetch('/api/admin/backup-restore/export', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: selectedExportType,
          format: exportFormat
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Export failed' }));
        throw new Error(errorData.error || `Export failed with status ${response.status}`);
      }

      // Get the filename from the response headers
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `${selectedExportType}_export.${exportFormat}`;

      // Download the file
      const blob = await response.blob();

      // Check blob size
      if (blob.size === 0) {
        throw new Error('Export file is empty');
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);

      showMessage('success', `Data exported successfully! (${(blob.size / 1024).toFixed(2)} KB)`);
      fetchExportHistory(); // Refresh history
      setSelectedExportType('');
    } catch (error) {
      console.error('Error exporting data:', error);
      showMessage('error', error instanceof Error ? error.message : 'Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  const handleCreateBackup = async () => {
    setIsCreatingBackup(true);

    try {
      const response = await fetch('/api/admin/backup-restore/backup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ type: 'full' })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Backup failed' }));
        throw new Error(errorData.error || `Backup failed with status ${response.status}`);
      }

      // Download the backup file
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `backup_${new Date().toISOString()}.json`;

      const blob = await response.blob();

      // Check blob size
      if (blob.size === 0) {
        throw new Error('Backup file is empty');
      }

      const sizeInMB = (blob.size / (1024 * 1024)).toFixed(2);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);

      showMessage('success', `Database backup created successfully! (${sizeInMB} MB)`);
    } catch (error) {
      console.error('Error creating backup:', error);
      showMessage('error', error instanceof Error ? error.message : 'Failed to create backup');
    } finally {
      setIsCreatingBackup(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file size (100MB limit)
      const MAX_FILE_SIZE = 100 * 1024 * 1024;
      if (file.size > MAX_FILE_SIZE) {
        showMessage('error', `File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB`);
        event.target.value = ''; // Reset input
        return;
      }

      // Validate file type
      if (!file.name.endsWith('.json')) {
        showMessage('error', 'Invalid file type. Only JSON backup files are supported');
        event.target.value = ''; // Reset input
        return;
      }

      setSelectedFile(file);
      setShowRestoreDialog(true);
    }
  };

  const handleRestore = async () => {
    if (!selectedFile) return;

    setIsRestoring(true);
    setShowRestoreDialog(false);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('mode', restoreMode);

      const response = await fetch('/api/admin/backup-restore/restore', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.details || 'Restore failed');
      }

      const successMsg = `Database restored successfully!\n${data.details.collections} collections\n${data.details.documents} documents restored`;
      showMessage('success', successMsg);
      setSelectedFile(null);
    } catch (error) {
      console.error('Error restoring backup:', error);
      const errorMsg = error instanceof Error ? error.message : 'Failed to restore backup';
      showMessage('error', errorMsg);
      setSelectedFile(null); // Reset file selection on error
    } finally {
      setIsRestoring(false);
    }
  };

  const toggleSchedule = async (id: string) => {
    const schedule = backupSchedules.find(s => s.id === id);
    if (!schedule) return;

    try {
      const response = await fetch('/api/admin/backup-restore/schedules', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id,
          isActive: !schedule.isActive
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update schedule');
      }

      setBackupSchedules(schedules =>
        schedules.map(s =>
          s.id === id ? { ...s, isActive: !s.isActive } : s
        )
      );
      showMessage('success', 'Schedule updated successfully');
    } catch (error) {
      console.error('Error updating schedule:', error);
      showMessage('error', 'Failed to update schedule');
    }
  };

  const addBackupSchedule = async () => {
    try {
      const response = await fetch('/api/admin/backup-restore/schedules', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: 'New Backup Schedule',
          frequency: 'daily',
          time: '02:00',
          isActive: true
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create schedule');
      }

      showMessage('success', 'Backup schedule created successfully');
      fetchBackupSchedules(); // Refresh schedules
    } catch (error) {
      console.error('Error creating schedule:', error);
      showMessage('error', 'Failed to create backup schedule');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'in-progress':
        return <RefreshCw className="h-5 w-5 text-blue-500 animate-spin" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getFrequencyLabel = (frequency: string) => {
    switch (frequency) {
      case 'daily':
        return 'Daily';
      case 'weekly':
        return 'Weekly';
      case 'monthly':
        return 'Monthly';
      default:
        return frequency;
    }
  };

  return (
    <div className="space-y-8">
      {/* Message Notification */}
      {message && (
        <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'} flex items-center space-x-2`}>
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5" />
          ) : (
            <AlertCircle className="h-5 w-5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Restore Dialog */}
      {showRestoreDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Restore Database</h3>
            <p className="text-sm text-gray-600 mb-4">
              You are about to restore the database from: <strong>{selectedFile?.name}</strong>
              <br />
              <span className="text-xs text-gray-500">
                File size: {selectedFile ? (selectedFile.size / 1024 / 1024).toFixed(2) : '0'} MB
              </span>
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Restore Mode
              </label>
              <select
                value={restoreMode}
                onChange={(e) => setRestoreMode(e.target.value as 'merge' | 'overwrite')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="merge">Merge (Keep existing data)</option>
                <option value="overwrite">Overwrite (Delete existing data)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {restoreMode === 'merge'
                  ? 'New data will be added alongside existing data'
                  : '⚠️ Warning: All existing data will be deleted!'}
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleRestore}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              >
                Confirm Restore
              </button>
              <button
                onClick={() => {
                  setShowRestoreDialog(false);
                  setSelectedFile(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Data Export Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Download className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">Data Export</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Export Type
            </label>
            <select
              value={selectedExportType}
              onChange={(e) => setSelectedExportType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none  focus:border-transparent"
            >
              <option value="">Select data type</option>
              {exportTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Export Format
            </label>
            <select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value as 'csv' | 'json')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none  focus:border-transparent"
            >
              <option value="csv">CSV</option>
              <option value="json">JSON</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={handleExport}
              disabled={!selectedExportType || isExporting}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isExporting ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span>Exporting...</span>
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  <span>Export Data</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Export History */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Exports</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Filename
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Size
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {exportHistory.map((export_) => (
                  <tr key={export_.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {export_.type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {export_.filename}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {export_.size}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(export_.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(export_.status)}
                        <span className="text-sm text-gray-900 capitalize">
                          {export_.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button className="text-blue-600 hover:text-blue-900">
                        Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Database Backup Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Database className="h-6 w-6 text-green-600" />
            <h2 className="text-xl font-semibold text-gray-900">Database Backup Scheduling</h2>
          </div>
          <button
            onClick={addBackupSchedule}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center space-x-2"
          >
            <Calendar className="h-4 w-4" />
            <span>Add Schedule</span>
          </button>
        </div>

        <div className="space-y-4">
          {backupSchedules.map((schedule) => (
            <div key={schedule.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={schedule.isActive}
                      onChange={() => toggleSchedule(schedule.id)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-900">
                      {schedule.name}
                    </span>
                  </div>

                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <span className="px-2 py-1 bg-gray-100 rounded-full">
                      {getFrequencyLabel(schedule.frequency)}
                    </span>
                    <span>at {schedule.time}</span>
                  </div>
                </div>

                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  {schedule.lastRun && (
                    <div>
                      <span className="font-medium">Last run:</span> {schedule.lastRun}
                    </div>
                  )}
                  {schedule.nextRun && (
                    <div>
                      <span className="font-medium">Next run:</span> {schedule.nextRun}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">Backup Information</p>
              <p className="mt-1">
                Automated backups are stored securely and can be restored in case of data loss.
                Manual backups can also be triggered from the dashboard at any time.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Manual Backup Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Database className="h-6 w-6 text-purple-600" />
          <h2 className="text-xl font-semibold text-gray-900">Manual Backup</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="text-center p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-400 transition-colors">
            <Database className="h-12 w-12 text-purple-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Create Full Backup</h3>
            <p className="text-sm text-gray-500 mb-4">
              Create a complete backup of the entire database including all tables and data
            </p>
            <button
              onClick={handleCreateBackup}
              disabled={isCreatingBackup}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 mx-auto"
            >
              {isCreatingBackup ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <Database className="h-4 w-4" />
                  <span>Start Full Backup</span>
                </>
              )}
            </button>
          </div>

          <div className="text-center p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-400 transition-colors">
            <RefreshCw className="h-12 w-12 text-purple-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Restore from Backup</h3>
            <p className="text-sm text-gray-500 mb-4">
              Restore the database from a previously created backup file
            </p>
            <label className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 cursor-pointer inline-flex items-center space-x-2">
              {isRestoring ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span>Restoring...</span>
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  <span>Choose Backup File</span>
                </>
              )}
              <input
                type="file"
                accept=".json"
                onChange={handleFileSelect}
                disabled={isRestoring}
                className="hidden"
              />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
